import type { ClientMessage, ServerMessage } from "@shared/protocol";
import { getToken, setToken } from "./sw.js";
import { authClient } from "./auth.js";

type Handler<T extends ServerMessage = ServerMessage> = (msg: T) => void;

export class WsClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private closed = false;
  private ready: Promise<void> | null = null;
  private reconnectDelayMs = 1000;
  // The `auth.ok` message is consumed by the connect handshake to resolve the
  // ready promise, so it never reaches the normal dispatch path. Remember it so
  // we can deliver it to `on('auth.ok')` subscribers — which is how the app
  // shell learns the session (otherwise the UI is stuck on "Connecting…").
  private lastAuthOk: Extract<ServerMessage, { type: "auth.ok" }> | null = null;

  async connect(): Promise<void> {
    if (this.ready) return this.ready;
    this.ready = this.doConnect();
    return this.ready;
  }

  private async doConnect(): Promise<void> {
    // getToken() REJECTS (not just resolves null) when the SW isn't
    // controlling the page yet — the exact state right after a fresh
    // full-page navigation, e.g. landing here straight off an OAuth
    // redirect. Treat that the same as "no token yet" so the recovery
    // fallback below actually runs instead of this whole call throwing
    // before it's reached.
    let token = await getToken().catch(() => null);
    if (!token) {
      // No token in the SW yet — this happens after OAuth sign-in (Google/
      // GitHub), since that flow is a full-page browser redirect, not an
      // XHR: better-auth sets a session cookie on the final redirect
      // response, but the page JS never runs to read that response's
      // headers the way it does for email/password sign-in's own XHR (see
      // lib/auth.ts's onSuccess hook). That header capture ALSO wouldn't
      // help here even if it could run: the bearer plugin only stamps
      // set-auth-token on a response that reissues the session cookie
      // (better-auth/dist/plugins/bearer's after-hook checks for
      // set-cookie), and a plain getSession() read of an already-fresh
      // session does neither — better-auth only refreshes the cookie near
      // its updateAge threshold, not on every read.
      //
      // Recovery instead reads the raw session token directly out of
      // getSession()'s JSON response body (session.token) — confirmed
      // equal to the bearer token's value (mod URL-encoding) by comparing
      // a real set-cookie vs set-auth-token pair from a sign-in response,
      // and the bearer plugin's own `before` hook accepts an unsigned raw
      // token exactly like this (it signs it server-side per request) —
      // and pushes it into the SW ourselves the same way setToken() is
      // used everywhere else, rather than depending on the header path
      // at all here.
      const result = await authClient.getSession().catch(() => null);
      const rawToken = result?.data?.session?.token;
      if (typeof rawToken === "string" && rawToken) {
        await setToken(rawToken).catch(() => {});
      }
      token = await getToken().catch(() => null);
    }
    if (!token) throw new Error("no auth token in SW — sign in first");

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws`;
    const ws = new WebSocket(url);

    await new Promise<void>((resolve, reject) => {
      ws.addEventListener("open", () => resolve(), { once: true });
      ws.addEventListener("error", (e) => reject(e), { once: true });
    });

    ws.send(
      JSON.stringify({ type: "auth.token", token } satisfies ClientMessage),
    );

    // Wait for auth.ok or closure
    await new Promise<void>((resolve, reject) => {
      const onMessage = (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data) as ServerMessage;
          if (msg.type === "auth.ok") {
            ws.removeEventListener("message", onMessage);
            this.lastAuthOk = msg;
            resolve();
          } else if (msg.type === "auth.error") {
            ws.removeEventListener("message", onMessage);
            reject(new Error(msg.message));
          }
        } catch {
          /* ignore */
        }
      };
      ws.addEventListener("message", onMessage);
      ws.addEventListener(
        "close",
        (e) => {
          reject(new Error(`WS closed before auth: ${e.code} ${e.reason}`));
        },
        { once: true },
      );
    });

    ws.addEventListener("message", (e) => this.dispatch(e));
    ws.addEventListener("close", () => this.handleClose());
    this.ws = ws;

    // Re-deliver the handshake's auth.ok to subscribers registered before
    // connect resolved (e.g. AppShell), so they receive the session.
    if (this.lastAuthOk) {
      const msg = this.lastAuthOk;
      for (const h of this.handlers) h(msg);
    }
  }

  private dispatch(e: MessageEvent): void {
    try {
      const msg = JSON.parse(e.data) as ServerMessage;
      for (const h of this.handlers) h(msg);
    } catch {
      // ignore malformed server messages
    }
  }

  private handleClose(): void {
    this.ws = null;
    this.ready = null;
    this.lastAuthOk = null;
    if (this.closed) return;
    // Reconnect with simple backoff (capped)
    setTimeout(() => {
      if (this.closed) return;
      this.connect().catch(() => {
        this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 30_000);
        this.handleClose();
      });
    }, this.reconnectDelayMs);
  }

  send(msg: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("WS not connected");
    }
    this.ws.send(JSON.stringify(msg));
  }

  on<K extends ServerMessage["type"]>(
    type: K,
    handler: (msg: Extract<ServerMessage, { type: K }>) => void,
  ): () => void {
    const wrapped: Handler = (msg) => {
      if (msg.type === type)
        handler(msg as Extract<ServerMessage, { type: K }>);
    };
    this.handlers.add(wrapped);
    // If the connection already authenticated, deliver the cached auth.ok so
    // late subscribers (registered after connect resolved) still get it.
    if (type === "auth.ok" && this.lastAuthOk) {
      const msg = this.lastAuthOk;
      queueMicrotask(() => {
        if (this.handlers.has(wrapped)) wrapped(msg);
      });
    }
    return () => this.handlers.delete(wrapped);
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
  }
}

// Singleton instance used across the app.
export const ws = new WsClient();
